/**
 * TeamRail — "From your team" (band 3 of the CM home, THIRD ON PURPOSE).
 *
 * Awareness-only: recent record_events on my clients by OTHERS. Every
 * line names the client; every line traces to a record event, and the
 * tracing stays out of the copy: "provenance / record event / feed" is
 * engine talk, not care language. The R5 billing line is deliberately
 * ABSENT: there is no cheap join from an issued invoice back to
 * encounter notes yet, and building billing substrate for a rail line
 * is out of scope.
 *
 * The heading carries today's count as a quiet chip ("2 today"): the
 * lines dated today, while the rail itself keeps its most recent lines
 * regardless of day. Absent at zero.
 *
 * Mobile: the rail collapses to a disclosure ("Recent activity from
 * your team") — the page renders this component inside a <details> on
 * narrow viewports, and the summary echoes the same count.
 */

import { useState, type ReactNode } from 'react';
import { Chip } from '@/components/primitives/Chip';
import type { CmHomeRailLineWire } from '@/lib/cmHomeApi';
import {
  ENTITY_LABELS,
  actorLabel,
  homeDayOf,
  proseDay,
  railLinesToday,
  yourNotePhrase,
} from '@/lib/cmHome';
import { MY_CLIENTS_HEADINGS } from '@/lib/headingCase';
import { QuietSection, ROW_CAP, ROW_LIST, SECTION_CARD, ShowAllToggle } from '@/components/home/RailParts';

function lineText(line: CmHomeRailLineWire): ReactNode {
  const who = <span className="font-semibold text-ink">{actorLabel(line.actor_name, line.actor_role)}</span>;
  const client = <span className="font-semibold text-sage-800">{line.client_display_name}</span>;
  switch (line.kind) {
    case 'direct_edit': {
      const section = line.entity ? (ENTITY_LABELS[line.entity] ?? 'the record') : 'the record';
      return (
        <>
          {who} edited <span className="font-semibold text-sage-800">{section}</span> directly for{' '}
          {client}
        </>
      );
    }
    case 'confirmed_updates':
      return (
        <>
          {who} confirmed {line.count} record {line.count === 1 ? 'update' : 'updates'} for {client}
        </>
      );
    case 'intake_seed':
      return (
        <>
          {who} built {client}
          {"'s"} record from an assessment
        </>
      );
    default:
      return (
        <>
          {who} added a case note for {client}
        </>
      );
  }
}

export interface TeamRailProps {
  rail: readonly CmHomeRailLineWire[];
  todayIso: string;
}

export function TeamRail({ rail, todayIso }: TeamRailProps): ReactNode {
  const today = railLinesToday(rail, todayIso);
  // LP-24: the first ROW_CAP lines, then "Show all N".
  const [showAll, setShowAll] = useState(false);
  if (rail.length === 0) {
    // Emptiness is quiet: the title and one line, no card.
    return (
      <section aria-labelledby="rail-heading" data-testid="team-rail">
        <QuietSection headingId="rail-heading" title={MY_CLIENTS_HEADINGS.fromYourTeam}>
          <p data-testid="rail-empty" className="text-title-sm text-ink-soft">
            No new activity from your team.
          </p>
        </QuietSection>
      </section>
    );
  }
  return (
    <section aria-labelledby="rail-heading" data-testid="team-rail" className={SECTION_CARD}>
      {/* LP-11: the My Clients rail's From Your Team; hairline rows. */}
      <div className="mb-2 flex h-7 items-center gap-2.5">
        <h2 id="rail-heading" className="flex h-7 items-center font-display text-lg leading-7 text-sage-800">
          {MY_CLIENTS_HEADINGS.fromYourTeam}
        </h2>
        {today > 0 && (
          <Chip variant="neutral" className="tabular-nums" data-testid="rail-today-count">
            {today} today
          </Chip>
        )}
      </div>
        <ul className={`m-0 list-none p-0 ${ROW_LIST}`}>
          {(showAll ? rail : rail.slice(0, ROW_CAP)).map((line) => {
            const when = proseDay(homeDayOf(line.at), todayIso);
            const whenCap = when.charAt(0).toUpperCase() + when.slice(1);
            return (
              <li key={line.id} data-testid="rail-line" className="py-2.5 text-title-sm text-ink-soft">
                {lineText(line)}
                <span className="mt-0.5 block text-[11.5px] text-ink-soft/80">
                  {whenCap}
                  {line.from_your_note_occurred_on !== null &&
                    ` · from ${yourNotePhrase(line.from_your_note_occurred_on, todayIso)}`}
                </span>
              </li>
            );
          })}
        </ul>
      <ShowAllToggle total={rail.length} open={showAll} onToggle={() => setShowAll((v) => !v)} testId="rail-show-all" />
    </section>
  );
}
